import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TenantService } from '../../services/tenant.service';
import {
  AdminAudienceGroup,
  AdminAudienceUser,
  AdminBroadcastRequest,
  AdminBroadcastResponse,
  NotificationMarkAllReadResponse,
  NotificationPreference,
  NotificationPreferenceUpdateRequest,
  NotificationReadResponse,
  NotificationUnreadCountResponse,
  PaginatedResponse,
  UserNotificationItem,
} from './notification.models';

interface NotificationListQuery {
  page?: number;
  page_size?: number;
  unread?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly tenantService: TenantService
  ) {}

  listNotifications(query: NotificationListQuery = {}): Observable<PaginatedResponse<UserNotificationItem>> {
    const params = this.buildParams(query);
    return this.http.get<PaginatedResponse<UserNotificationItem>>(`${this.apiBase}/notifications/`, { params });
  }

  getNotification(notificationId: number): Observable<UserNotificationItem> {
    return this.http.get<UserNotificationItem>(`${this.apiBase}/notifications/${notificationId}/`);
  }

  sendAdminBroadcast(payload: AdminBroadcastRequest): Observable<AdminBroadcastResponse> {
    return this.http.post<AdminBroadcastResponse>(`${this.apiBase}/admin/notifications/broadcast/`, payload);
  }

  listAdminAudienceUsers(search = '', pageSize = 20): Observable<PaginatedResponse<AdminAudienceUser>> {
    let params = new HttpParams()
      .set('page', '1')
      .set('page_size', String(pageSize));

    const normalizedSearch = search.trim();
    if (normalizedSearch) {
      params = params.set('search', normalizedSearch);
    }

    return this.http.get<PaginatedResponse<AdminAudienceUser>>(`${this.apiBase}/users/`, { params });
  }

  listAdminAudienceGroups(): Observable<PaginatedResponse<AdminAudienceGroup> | AdminAudienceGroup[]> {
    const params = new HttpParams().set('page_size', '200');
    return this.http.get<PaginatedResponse<AdminAudienceGroup> | AdminAudienceGroup[]>(`${this.apiBase}/training-groups/`, { params });
  }

  getUnreadCount(): Observable<NotificationUnreadCountResponse> {
    return this.http.get<NotificationUnreadCountResponse>(`${this.apiBase}/notifications/unread-count/`);
  }

  markAsRead(notificationId: number): Observable<NotificationReadResponse> {
    return this.http.patch<NotificationReadResponse>(`${this.apiBase}/notifications/${notificationId}/read/`, {});
  }

  markAllAsRead(): Observable<NotificationMarkAllReadResponse> {
    return this.http.post<NotificationMarkAllReadResponse>(`${this.apiBase}/notifications/mark-all-read/`, {});
  }

  getPreferences(): Observable<NotificationPreference[]> {
    return this.http.get<NotificationPreference[]>(`${this.apiBase}/notifications/preferences/`);
  }

  updatePreferences(payload: NotificationPreferenceUpdateRequest): Observable<NotificationPreference> {
    return this.http.patch<NotificationPreference>(`${this.apiBase}/notifications/preferences/`, payload);
  }

  private get apiBase(): string {
    return `${this.tenantService.getApiBaseUrl()}/api`;
  }

  private buildParams(source: NotificationListQuery): HttpParams {
    let params = new HttpParams();

    Object.entries(source).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      params = params.set(key, String(value));
    });

    return params;
  }
}
